const ROOT_SELECTOR = '[data-legacy-pdp-story]';
const ACTION_SELECTOR = '[data-action^="legacy-pdp:"]';
const storyStates = new WeakMap();
const pathogenStates = new WeakMap();

function getRoot(element) {
    return element?.closest(ROOT_SELECTOR) || null;
}

function getStoryElements(root) {
    const carousel = root.querySelector('[data-section="legacy-pdp:stories"]');
    if (!carousel) return null;

    return {
        carousel,
        track: carousel.querySelector('.vt-track'),
        viewport: carousel.querySelector('.vt-viewport'),
        cards: Array.from(carousel.querySelectorAll('.vt-card')),
    };
}

function warmStoryVideos(elements, activeIndex) {
    [activeIndex - 1, activeIndex, activeIndex + 1].forEach((index) => {
        const video = elements.cards[index]?.querySelector('.vt-vid');
        if (!video || video.preload === 'auto') return;

        video.preload = 'auto';
        if (video.readyState === 0) video.load();
    });
}

function updateStory(root, nextIndex, restartActive = false) {
    const elements = getStoryElements(root);
    if (!elements?.cards.length || !elements.track || !elements.viewport) return;

    const state = storyStates.get(root);
    if (!state) return;

    const count = elements.cards.length;
    state.activeIndex = Math.max(0, Math.min(count - 1, nextIndex));

    const activeCard = elements.cards[state.activeIndex];
    const cardWidth = activeCard.offsetWidth;
    const gap = parseFloat(getComputedStyle(elements.track).gap) || 22;
    const offset = elements.viewport.clientWidth / 2
        - (state.activeIndex * (cardWidth + gap) + cardWidth / 2);

    elements.track.style.transform = `translateX(${offset}px)`;
    elements.cards.forEach((card, index) => {
        const video = card.querySelector('.vt-vid');
        const isActive = index === state.activeIndex;
        card.dataset.state = isActive ? 'active' : 'inactive';
        card.classList.toggle('is-active', isActive);
        if (!video) return;

        video.muted = state.isMuted;

        if (!isActive) {
            video.pause();
            return;
        }

        video.preload = 'auto';
        if (video.readyState === 0) video.load();
        if (restartActive) {
            try {
                video.currentTime = 0;
            } catch (_) {}
        }
        if (state.isVisible) video.play().catch(() => {});
    });

    warmStoryVideos(elements, state.activeIndex);
}

function snapStory(root) {
    const elements = getStoryElements(root);
    const state = storyStates.get(root);
    if (!elements?.cards.length || !elements.track || !state?.originalCount) return 0;

    const count = state.originalCount;
    if (state.activeIndex >= count && state.activeIndex < count * 2) return 0;

    const previousIndex = state.activeIndex;
    const nextIndex = ((previousIndex % count) + count) % count + count;
    const previousVideo = elements.cards[previousIndex]?.querySelector('.vt-vid');
    const nextVideo = elements.cards[nextIndex]?.querySelector('.vt-vid');

    if (previousVideo && nextVideo) {
        try {
            nextVideo.currentTime = previousVideo.currentTime || 0;
        } catch (_) {}
        previousVideo.pause();
    }

    elements.track.style.transition = 'none';
    updateStory(root, nextIndex);
    void elements.track.offsetHeight;
    elements.track.style.transition = '';

    return nextIndex - previousIndex;
}

function hydrateStories(root) {
    const elements = getStoryElements(root);
    if (!elements?.cards.length || storyStates.has(root)) return;

    const originalCards = elements.cards;
    const originalCount = originalCards.length;
    const originalActiveIndex = Math.max(0, originalCards.findIndex((card) => card.dataset.state === 'active'));

    originalCards.map((card) => card.cloneNode(true)).reverse().forEach((card) => elements.track.prepend(card));
    originalCards.map((card) => card.cloneNode(true)).forEach((card) => elements.track.append(card));

    const loopElements = getStoryElements(root);
    const state = {
        activeIndex: originalCount + originalActiveIndex,
        originalCount,
        isVisible: false,
        isMuted: true,
    };
    storyStates.set(root, state);

    // Media completion does not bubble, so each repeated clip needs its own
    // bounded listener. Advancing from the active clip mirrors the new PDP.
    loopElements.cards.forEach((card, index) => {
        const video = card.querySelector('.vt-vid');
        if (!video) return;

        video.addEventListener('ended', () => {
            const currentState = storyStates.get(root);
            if (!currentState?.isVisible || currentState.activeIndex !== index) return;

            snapStory(root);
            updateStory(root, currentState.activeIndex + 1, true);
        });

        video.addEventListener('timeupdate', () => {
            if (storyStates.get(root)?.activeIndex !== index || !video.duration) return;

            card.querySelector('.vt-progress span')?.style.setProperty(
                'width',
                `${video.currentTime / video.duration * 100}%`
            );
        });
    });

    const observer = new IntersectionObserver((entries) => {
        state.isVisible = entries.some((entry) => entry.isIntersecting);
        updateStory(root, state.activeIndex);
    }, { threshold: 0.3 });

    observer.observe(elements.carousel);
    updateStory(root, state.activeIndex);
}

function updatePathogenPage(root, nextIndex, direction = 'next') {
    const carousel = root.querySelector('[data-section="legacy-pdp:pathogens"]');
    if (!carousel) return;

    const pages = Array.from(carousel.querySelectorAll('.lp-page'));
    const dots = Array.from(root.querySelectorAll('[data-action="legacy-pdp:pathogen-page"]'));
    if (!pages.length) return;

    const index = (nextIndex % pages.length + pages.length) % pages.length;
    carousel.dataset.direction = direction;
    carousel.dataset.dir = direction;
    carousel.dataset.page = String(index);
    carousel.classList.add('in-view');

    pages.forEach((page, pageIndex) => {
        const isActive = pageIndex === index;
        page.dataset.state = isActive ? 'active' : 'inactive';
        page.classList.toggle('is-active', isActive);
        page.hidden = !isActive;
        page.setAttribute('aria-hidden', String(!isActive));
    });

    dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === index;
        dot.dataset.state = isActive ? 'active' : 'inactive';
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', String(isActive));
    });

    pathogenStates.set(root, index);
}

function hydratePathogens(root) {
    if (pathogenStates.has(root)) return;
    pathogenStates.set(root, 0);
    updatePathogenPage(root, 0);
}

function updateConcernTab(button) {
    const root = getRoot(button);
    const tabList = button.closest('[role="tablist"]');
    const controls = button.getAttribute('aria-controls');
    if (!root || !tabList || !controls) return;

    const tabs = Array.from(tabList.querySelectorAll('[data-action="legacy-pdp:concern-tab"]'));
    tabs.forEach((tab) => {
        const isActive = tab === button;
        const panelId = tab.getAttribute('aria-controls');
        const panel = panelId ? root.querySelector(`#${CSS.escape(panelId)}`) : null;
        tab.setAttribute('aria-selected', String(isActive));
        if (panel) panel.hidden = !isActive;
    });
}

function updateHeadSwatch(button) {
    const card = button.closest('.hp-card');
    const group = button.closest('.hp-swatches');
    const image = card?.querySelector('.hp-img img');
    if (!card || !group || !image) return;

    image.src = button.dataset.img || image.src;

    const variantInput = card.querySelector('input[name="id"]');
    const sellingPlanInput = card.querySelector('[data-head-selling-plan]');
    const submitButton = card.querySelector('.hp-subscribe-button');

    if (variantInput && button.dataset.variantId) variantInput.value = button.dataset.variantId;
    if (sellingPlanInput) sellingPlanInput.value = button.dataset.sellingPlanId || '';
    if (submitButton) {
        submitButton.disabled = !button.dataset.variantId || !button.dataset.sellingPlanId;
    }

    group.querySelectorAll('[data-action="legacy-pdp:head-swatch"]').forEach((swatch) => {
        swatch.setAttribute('aria-pressed', String(swatch === button));
    });
}

function createLabModal() {
    const existing = document.querySelector('.lr-modal[data-legacy-lab-modal]');
    if (existing) return existing;

    const groups = [
        {
            title: 'Kills 99.9% of:',
            note: 'Brush head sealed in the UV-C base for one 12-minute cycle.',
            items: [
                ['E. coli', 'ATCC 8739', '&gt;99.9%', '≥5.3-log', 'Intertek · Mar 2021'],
                ['Salmonella', 'CMCC 50094', '&gt;99.9%', '≥5.1-log', 'Intertek · Mar 2021'],
                ['Streptococcus', 'ATCC 19615', '&gt;99.92%', '&gt;3.0-log', 'Microchem · 2020'],
                ['Candida albicans', 'ATCC 10231', '99.99%', '≈4.2-log', 'Gmicro · 2026'],
            ],
        },
        {
            title: 'Viruses inactivated',
            note: 'The same 12-minute UV-C cycle, measured as reduction in viral titre.',
            items: [
                ['Influenza A (H1N1)', 'ATCC VR-1469', '≥99.99%', '≥4.0-log', 'Microchem · 2020'],
                ['Rhinovirus 37', 'ATCC VR-1607', '99.90%', '3.1-log', 'Microchem · 2020'],
            ],
        },
        {
            title: 'Whitening &amp; plaque removal',
            note: 'How the brush itself performs against a manual toothbrush.',
            items: [
                ['Two-shade whitening', '', '7.6', 'VITA shades whiter in 2 weeks', 'Intertek CRS · Aug 2021'],
                ['Interproximal cleaning', '', '12×', 'more plaque removed vs. manual', 'Intertek CRS · Jan 2023'],
            ],
        },
    ];

    const groupMarkup = groups.map((group) => `
        <section class="lr-group">
            <div class="lr-group-head"><h3>${group.title}</h3><p>${group.note}</p></div>
            <ul class="lr-list">
                ${group.items.map((item) => `
                    <li class="lr-row">
                        <span class="lr-org"><span class="lr-name">${item[0]}</span><small>${item[1]}</small></span>
                        <span class="lr-chip"><b>${item[2]}</b><small>${item[3]}</small></span>
                        <span class="lr-info"><span class="lr-lab">${item[4]}</span></span>
                    </li>
                `).join('')}
            </ul>
        </section>
    `).join('');

    const modal = document.createElement('div');
    modal.className = 'lr-modal';
    modal.hidden = true;
    modal.dataset.legacyLabModal = '';
    modal.innerHTML = `
        <div class="lr-backdrop" data-action="legacy-pdp:lab-close"></div>
        <div class="lr-panel" role="dialog" aria-modal="true" aria-labelledby="legacy-lr-title" tabindex="-1">
            <header class="lr-head">
                <div>
                    <p class="lr-kicker">Independent lab results</p>
                    <h2 id="legacy-lr-title">The proof, in full.</h2>
                    <p class="lr-intro">Independent laboratories tested the TAO Clean UV-C base and brush against real pathogens and a manual toothbrush.</p>
                </div>
                <button class="lr-close" type="button" data-action="legacy-pdp:lab-close" aria-label="Close lab reports">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
                </button>
            </header>
            <div class="lr-body">${groupMarkup}</div>
            <p class="lr-foot">Results reflect independent testing of the TAO Clean UV-C base and brush heads.</p>
        </div>
    `;

    modal.addEventListener('click', (event) => {
        if (!event.target.closest('[data-action="legacy-pdp:lab-close"]')) return;
        event.preventDefault();
        event.stopPropagation();
        closeLabModal();
    });

    document.body.appendChild(modal);
    return modal;
}

function openLabModal(trigger) {
    const modal = createLabModal();
    modal.dataset.returnFocusId = trigger.id || '';
    modal.hidden = false;
    modal.dataset.state = 'open';
    modal.classList.add('is-open');
    document.body.classList.add('lr-open');
    document.body.dataset.legacyLabOpen = 'true';
    modal.querySelector('.lr-panel')?.focus({ preventScroll: true });
}

function closeLabModal() {
    const modal = document.querySelector('.lr-modal[data-legacy-lab-modal]');
    if (!modal || modal.hidden) return;

    modal.dataset.state = 'closed';
    modal.classList.remove('is-open');
    modal.hidden = true;
    document.body.classList.remove('lr-open');
    delete document.body.dataset.legacyLabOpen;
}

function handleStoryPrevious(action) {
    const root = getRoot(action);
    const state = root && storyStates.get(root);
    if (!root || !state) return;

    snapStory(root);
    updateStory(root, state.activeIndex - 1, true);
}

function handleStoryNext(action) {
    const root = getRoot(action);
    const state = root && storyStates.get(root);
    if (!root || !state) return;

    snapStory(root);
    updateStory(root, state.activeIndex + 1, true);
}

function handleStorySelect(action) {
    const root = getRoot(action);
    const state = root && storyStates.get(root);
    const elements = root && getStoryElements(root);
    const index = elements?.cards.indexOf(action) ?? -1;
    if (!root || !state || index < 0) return;

    const indexShift = snapStory(root);
    updateStory(root, index + indexShift, true);
}

function handleStoryMute(action) {
    const root = getRoot(action);
    const state = root && storyStates.get(root);
    if (!root || !state) return;

    state.isMuted = !state.isMuted;
    const elements = getStoryElements(root);
    elements?.cards.forEach((card) => {
        const video = card.querySelector('.vt-vid');
        const button = card.querySelector('[data-action="legacy-pdp:story-mute"]');
        video.muted = state.isMuted;
        button?.setAttribute('aria-label', state.isMuted ? 'Unmute' : 'Mute');
        button?.querySelector('use')?.setAttribute('href', state.isMuted ? '#ic-mute' : '#ic-volume');
    });
}

function handlePathogenPrevious(action) {
    const root = getRoot(action);
    const index = root ? pathogenStates.get(root) || 0 : 0;
    if (root) updatePathogenPage(root, index - 1, 'prev');
}

function handlePathogenNext(action) {
    const root = getRoot(action);
    const index = root ? pathogenStates.get(root) || 0 : 0;
    if (root) updatePathogenPage(root, index + 1, 'next');
}

function handlePathogenPage(action) {
    const root = getRoot(action);
    const current = root ? pathogenStates.get(root) || 0 : 0;
    const index = Number(action.dataset.pageIndex || 0);
    if (root) updatePathogenPage(root, index, index >= current ? 'next' : 'prev');
}

const ACTION_HANDLERS = new Map([
    ['legacy-pdp:story-prev', handleStoryPrevious],
    ['legacy-pdp:story-next', handleStoryNext],
    ['legacy-pdp:story-select', handleStorySelect],
    ['legacy-pdp:story-mute', handleStoryMute],
    ['legacy-pdp:pathogen-prev', handlePathogenPrevious],
    ['legacy-pdp:pathogen-next', handlePathogenNext],
    ['legacy-pdp:pathogen-page', handlePathogenPage],
    ['legacy-pdp:concern-tab', updateConcernTab],
    ['legacy-pdp:head-swatch', updateHeadSwatch],
    ['legacy-pdp:lab-open', openLabModal],
    ['legacy-pdp:lab-close', closeLabModal],
]);

function handleDocumentClick(event) {
    const action = event.target.closest(ACTION_SELECTOR);
    if (!action) return;

    const handler = ACTION_HANDLERS.get(action.dataset.action);
    if (!handler) return;

    event.preventDefault();
    handler(action);
}

function handleDocumentKeydown(event) {
    if (event.key === 'Escape') closeLabModal();

    const tab = event.target.closest?.('[data-action="legacy-pdp:concern-tab"]');
    if (!tab || !['Home', 'End'].includes(event.key)) return;

    const tabs = Array.from(tab.closest('[role="tablist"]')?.querySelectorAll('[data-action="legacy-pdp:concern-tab"]') || []);
    if (!tabs.length) return;

    event.preventDefault();
    const nextTab = event.key === 'Home' ? tabs[0] : tabs[tabs.length - 1];
    nextTab.focus();
    updateConcernTab(nextTab);
}

function handleResize() {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
        const state = storyStates.get(root);
        if (state) updateStory(root, state.activeIndex);
    });
}

function hydrateSection(root) {
    if (!(root instanceof Element) || !root.matches(ROOT_SELECTOR)) return;
    hydrateStories(root);
    hydratePathogens(root);
    root.dataset.state = 'ready';
}

function handleSectionLoad(event) {
    event.target.querySelectorAll?.(ROOT_SELECTOR).forEach(hydrateSection);
}

function initLegacyPdpStory() {
    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('keydown', handleDocumentKeydown);
    document.addEventListener('shopify:section:load', handleSectionLoad);
    window.addEventListener('resize', handleResize);
    document.querySelectorAll(ROOT_SELECTOR).forEach(hydrateSection);
}

initLegacyPdpStory();
